import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import api, { errMsg } from '@/lib/api';
import { Sparkles, Trash2, Plus, Loader2, Info, Lightbulb } from 'lucide-react';

const TYPE_LABEL = {
  PAYMENT_ACCOUNT: 'Payment account',
  PAYMENT_METHOD: 'Payment method',
  SALESPERSON: 'Salesperson',
  ITEM_DESCRIPTION: 'Item description',
  CATEGORY_SHORTHAND: 'Category shorthand',
};
const TYPE_CLS = {
  PAYMENT_ACCOUNT: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
  PAYMENT_METHOD: 'bg-cyan-500/15 text-cyan-600 border-cyan-500/30',
  SALESPERSON: 'bg-violet-500/15 text-violet-600 border-violet-500/30',
  ITEM_DESCRIPTION: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  CATEGORY_SHORTHAND: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
};

export default function WaImportSettings() {
  const navigate = useNavigate();
  const { user } = useApp();
  const [rules, setRules] = useState([]);
  const [types, setTypes] = useState(Object.keys(TYPE_LABEL));
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ alias_text: '', normalized_value: '', alias_type: 'PAYMENT_ACCOUNT' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/wa-alias-rules');
      const d = r.data.data;
      setRules(d.rules || []);
      setTypes(d.types || Object.keys(TYPE_LABEL));
      setCanManage(!!d.can_manage);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addRule = useCallback(async () => {
    if (!form.alias_text.trim() || !form.normalized_value.trim()) {
      toast.error('Enter both the shorthand and what it means.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/wa-alias-rules', {
        alias_text: form.alias_text, normalized_value: form.normalized_value, alias_type: form.alias_type,
      });
      toast.success('Alias saved.');
      setForm({ alias_text: '', normalized_value: '', alias_type: form.alias_type });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  }, [form, load]);

  const toggleRule = useCallback(async (rule, active) => {
    setRules((cur) => cur.map((r) => (r.id === rule.id ? { ...r, active } : r)));
    try {
      await api.post(`/wa-alias-rules/${rule.id}/toggle`, { active });
    } catch (e) {
      toast.error(errMsg(e));
      setRules((cur) => cur.map((r) => (r.id === rule.id ? { ...r, active: !active } : r)));
    }
  }, []);

  const deleteRule = useCallback(async (rule) => {
    const prev = rules;
    setRules((cur) => cur.filter((r) => r.id !== rule.id));
    try {
      await api.delete(`/wa-alias-rules/${rule.id}`);
      toast.message('Alias deleted.');
    } catch (e) {
      toast.error(errMsg(e));
      setRules(prev);
    }
  }, [rules]);

  return (
    <Page>
      <TopBar title="WhatsApp Import Settings" onBack={() => navigate('/paste-whatsapp-sales')} />
      <div className="px-4 pt-4 pb-40 max-w-3xl mx-auto">
        <div className="mb-4 flex items-start gap-2 rounded-2xl border border-border bg-muted/30 p-3" data-testid="wa-alias-intro">
          <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-muted-foreground">
            Teach SHABZ your team&apos;s shorthand so future imports read correctly — e.g. <span className="font-mono">ICIC → ICICI</span> or
            <span className="font-mono"> muskan 2 → Muskan2</span>. Learned aliases only help parsing; they can never change a price,
            total or discount.
          </p>
        </div>

        {/* Add form (Owner/Manager only) */}
        {canManage && (
          <div className="mb-5 rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid="wa-alias-add-card">
            <p className="mb-3 flex items-center gap-1.5 text-sm font-semibold">
              <Sparkles className="h-4 w-4 text-primary" /> Add a learned alias
            </p>
            <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
              <div>
                <Label className="text-[11px] text-muted-foreground">Shorthand (as typed in WhatsApp)</Label>
                <Input data-testid="wa-alias-text-input" value={form.alias_text}
                  onChange={(e) => setForm((f) => ({ ...f, alias_text: e.target.value }))}
                  placeholder="e.g. icic" className="mt-1 h-10 rounded-xl" />
              </div>
              <div>
                <Label className="text-[11px] text-muted-foreground">Means</Label>
                <Input data-testid="wa-alias-value-input" value={form.normalized_value}
                  onChange={(e) => setForm((f) => ({ ...f, normalized_value: e.target.value }))}
                  placeholder="e.g. ICICI" className="mt-1 h-10 rounded-xl" />
              </div>
              <div className="sm:w-44">
                <Label className="text-[11px] text-muted-foreground">Type</Label>
                <Select value={form.alias_type} onValueChange={(v) => setForm((f) => ({ ...f, alias_type: v }))}>
                  <SelectTrigger data-testid="wa-alias-type-select" className="mt-1 h-10 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t} value={t} data-testid={`wa-alias-type-opt-${t}`}>{TYPE_LABEL[t] || t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button data-testid="wa-alias-add-btn" onClick={addRule} disabled={saving}
              className="mt-3 h-10 rounded-xl font-semibold">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Save alias
            </Button>
          </div>
        )}

        {/* Rules list */}
        <div className="rounded-2xl border border-border bg-card shadow-sm" data-testid="wa-alias-list">
          <p className="border-b border-border px-4 py-3 text-sm font-semibold">Learned aliases ({rules.length})</p>
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rules.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-8 text-center" data-testid="wa-alias-empty">
              <Info className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No learned aliases yet.</p>
              <p className="text-xs text-muted-foreground">Confirm a correction during import and tap “Remember”, or add one above.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Alias</TableHead>
                  <TableHead>Meaning</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-center">Used</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  {canManage && <TableHead className="text-right">·</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r.id} data-testid={`wa-alias-row-${r.id}`}>
                    <TableCell className="font-mono text-xs">{r.alias_text}</TableCell>
                    <TableCell className="text-sm font-medium">{r.normalized_value}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${TYPE_CLS[r.alias_type] || ''}`}>
                        {TYPE_LABEL[r.alias_type] || r.alias_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-xs text-muted-foreground" data-testid={`wa-alias-usage-${r.id}`}>{r.usage_count || 0}</TableCell>
                    <TableCell className="text-center">
                      <Switch checked={!!r.active} disabled={!canManage}
                        data-testid={`wa-alias-toggle-${r.id}`}
                        onCheckedChange={(v) => toggleRule(r, v)} />
                    </TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        <button type="button" data-testid={`wa-alias-delete-${r.id}`} onClick={() => deleteRule(r)}
                          className="text-muted-foreground hover:text-rose-500" aria-label="Delete alias">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        {!canManage && (
          <p className="mt-3 text-center text-[11px] text-muted-foreground" data-testid="wa-alias-readonly">
            Only Owner or Manager can add, edit or remove learned aliases.
          </p>
        )}
      </div>
    </Page>
  );
}
